import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserTie,
    faArrowLeft,
    faArrowRight,
    faInfoCircle,
    faCheckCircle,
    faStore,
    faUsers,
    faGift,
    faBullhorn,
    faQrcode
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function UsuariosTrabajador() {
    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center mb-4">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                            <FontAwesomeIcon icon={faUserTie} className="text-blue-600 text-3xl" />
                        </div>
                        <div>
                            <h1 className="text-4xl font-bold text-gray-800 mb-2">
                                Trabajador
                            </h1>
                            <p className="text-xl text-gray-600">
                                Personal de tienda con acceso limitado para gestionar clientes y operaciones diarias.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Introducción */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-3 flex items-center">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-blue-600" />
                        ¿Qué es un Trabajador?
                    </h2>
                    <p className="text-gray-700 mb-4">
                        Un <strong>Trabajador</strong> es un miembro del personal de tu tienda que tiene acceso limitado a PetitPrime para realizar operaciones diarias como gestionar clientes, acumular puntos y canjear premios. Los trabajadores no tienen acceso a configuraciones, estadísticas o funciones de marketing.
                    </p>
                    <p className="text-gray-700">
                        Este rol es ideal para empleados que necesitan gestionar clientes en el punto de venta pero no requieren acceso a información estratégica o configuraciones del negocio.
                    </p>
                </div>

                {/* Acceso */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        ¿Cómo acceder?
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Los trabajadores acceden a PetitPrime de manera diferente a los dueños:
                        </p>
                        <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                            <li>El dueño de la marca debe agregarte como trabajador a una tienda desde la gestión de tiendas</li>
                            <li>Recibirás un email con las instrucciones para registrarte</li>
                            <li>Haz clic en el enlace del email o ve a la página de login de trabajadores</li>
                            <li>Completa el formulario de registro con tus datos</li>
                            <li>Inicia sesión con tu email y contraseña</li>
                            <li>Serás redirigido a tu dashboard de trabajador</li>
                        </ol>
                        <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                            <p className="text-sm text-gray-700">
                                <strong>Nota:</strong> Solo puedes acceder a las tiendas que el dueño te haya asignado. No podrás ver ni gestionar otras tiendas.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Dashboard de Trabajador */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        1. Dashboard de Trabajador
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Al iniciar sesión, verás tu dashboard personalizado con:
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faStore} className="mr-2 text-blue-600" />
                                    Tiendas Asociadas
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Número total de tiendas que tienes asignadas y a las que puedes acceder.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faBullhorn} className="mr-2 text-green-600" />
                                    Campañas Activas
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Número de campañas de juego o promociones activas en tus tiendas.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-purple-600" />
                                    Última Actividad
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Información sobre tu última actividad en el sistema.
                                </p>
                            </div>
                        </div>

                        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                            <p className="text-sm text-gray-700">
                                <strong>💡 Tip:</strong> Desde el dashboard puedes acceder rápidamente a todas tus tiendas asignadas y ver las campañas activas disponibles.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Funcionalidades Disponibles */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        2. Funcionalidades Disponibles
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Como trabajador, tienes acceso a las siguientes funcionalidades:
                        </p>

                        <div className="space-y-4">
                            {/* Gestionar Clientes */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faUsers} className="mr-2 text-blue-600" />
                                    Gestionar Clientes
                                </h3>
                                <p className="text-sm text-gray-700 mb-2">
                                    Puedes ver y gestionar los clientes de las tiendas asignadas:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Ver lista de clientes de cada tienda</li>
                                    <li>Buscar clientes por nombre, email o teléfono</li>
                                    <li>Crear nuevos clientes manualmente</li>
                                    <li>Ver historial de compras de cada cliente</li>
                                    <li>Ver puntos acumulados por cada cliente</li>
                                </ul>
                            </div>

                            {/* Acumular Puntos */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faGift} className="mr-2 text-green-600" />
                                    Acumular Puntos
                                </h3>
                                <p className="text-sm text-gray-700 mb-2">
                                    Puedes acumular puntos a los clientes cuando realizan compras:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Escanear el código QR del ticket del cliente</li>
                                    <li>Ingresar manualmente el número de ticket</li>
                                    <li>Los puntos se acumulan automáticamente según las reglas configuradas</li>
                                    <li>Ver confirmación de puntos acumulados</li>
                                </ul>
                            </div>

                            {/* Canjear Premios */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faGift} className="mr-2 text-purple-600" />
                                    Canjear Premios
                                </h3>
                                <p className="text-sm text-gray-700 mb-2">
                                    Puedes ayudar a los clientes a canjear sus premios:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Ver premios disponibles para cada cliente</li>
                                    <li>Canjear premios usando los puntos del cliente</li>
                                    <li>Aplicar descuentos directamente a los tickets</li>
                                    <li>Ver historial de canjes realizados</li>
                                </ul>
                            </div>

                            {/* Enviar Códigos de Campañas */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faQrcode} className="mr-2 text-orange-600" />
                                    Enviar Códigos de Campañas
                                </h3>
                                <p className="text-sm text-gray-700 mb-2">
                                    Si hay campañas activas en tus tiendas, puedes enviar códigos de recompensa:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li>Ver campañas activas en cada tienda</li>
                                    <li>Enviar códigos de recompensa a clientes</li>
                                    <li>Los códigos se envían por WhatsApp o SMS</li>
                                    <li>Gestionar el envío de códigos desde el dashboard</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Limitaciones */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        3. Limitaciones
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Como trabajador, <strong>NO</strong> tienes acceso a:
                        </p>
                        <ul className="space-y-3">
                            <li className="flex items-start">
                                <span className="text-red-500 mr-3 font-bold">✗</span>
                                <div>
                                    <strong className="text-gray-800">Configuración de marcas y tiendas:</strong> No puedes crear, editar o eliminar marcas o tiendas.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="text-red-500 mr-3 font-bold">✗</span>
                                <div>
                                    <strong className="text-gray-800">Estadísticas y reportes:</strong> No puedes ver estadísticas, métricas o reportes de las tiendas.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="text-red-500 mr-3 font-bold">✗</span>
                                <div>
                                    <strong className="text-gray-800">Marketing y campañas:</strong> No puedes crear ofertas, configurar WinBack, o gestionar campañas de marketing.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="text-red-500 mr-3 font-bold">✗</span>
                                <div>
                                    <strong className="text-gray-800">Gestión de productos:</strong> No puedes agregar, editar o eliminar productos del catálogo.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <span className="text-red-500 mr-3 font-bold">✗</span>
                                <div>
                                    <strong className="text-gray-800">Otras tiendas:</strong> Solo puedes acceder a las tiendas que te hayan sido asignadas específicamente.
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Mejores Prácticas */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        4. Mejores Prácticas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-blue-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Verifica siempre el cliente:</strong> Antes de acumular puntos o canjear premios, asegúrate de que estás trabajando con el cliente correcto.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-blue-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Escanea los códigos QR:</strong> Usa el escáner de códigos QR siempre que sea posible para evitar errores al ingresar números de ticket manualmente.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-blue-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Crea clientes cuando sea necesario:</strong> Si un cliente nuevo quiere acumular puntos, créalo en el sistema antes de procesar su compra.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-blue-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Informa sobre premios disponibles:</strong> Ayuda a los clientes a conocer qué premios pueden canjear con sus puntos.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-blue-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Usa las campañas activas:</strong> Si hay campañas activas, aprovecha para enviar códigos de recompensa a los clientes y aumentar su satisfacción.
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Troubleshooting */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        5. Solución de Problemas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="space-y-4">
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">No puedo iniciar sesión</h4>
                                <p className="text-gray-700 text-sm">
                                    Verifica que el dueño te haya agregado como trabajador a una tienda. Si acabas de ser agregado, espera unos minutos y vuelve a intentar. Si el problema persiste, contacta con el dueño de la marca.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">No veo ninguna tienda en mi dashboard</h4>
                                <p className="text-gray-700 text-sm">
                                    Esto significa que aún no te han asignado a ninguna tienda. Contacta con el dueño de la marca para que te asigne a una tienda.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">No puedo acumular puntos a un cliente</h4>
                                <p className="text-gray-700 text-sm">
                                    Verifica que el cliente esté correctamente registrado en el sistema y que el ticket sea válido. Si el problema persiste, contacta con el dueño de la marca.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Quiero ver estadísticas pero no tengo acceso</h4>
                                <p className="text-gray-700 text-sm">
                                    Los trabajadores no tienen acceso a estadísticas por diseño. Si necesitas ver estadísticas, habla con el dueño de la marca o considera convertirte en franquiciado si eres dueño de una franquicia.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navegación */}
                <div className="flex justify-between items-center pt-8 border-t">
                    <Link
                        to="/documentation/usuarios/dueño"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Anterior: Dueño de la Marca
                    </Link>
                    <Link
                        to="/documentation/usuarios/franquiciado"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                    >
                        Siguiente: Franquiciado
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default UsuariosTrabajador;

